import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal, Button, Input, Form, Select, Typography } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import * as Yup from "yup";
import { ColumnsType } from "antd/lib/table";
import { PDFDocument } from "pdf-lib";
import { id } from "date-fns/locale";
import ButtonDownload from "../../Button";
import { removeUndefinedKeys } from "../../../utils/formatters";
import { IActionModalProps } from "..";
import { useToast } from "../../../hooks/toast";
import { Flex, InputContent, InputDefault, Label, TotalHours } from "./styles";
import api from "../../../services/api";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../FormItem";
import ProjectTypes from "../../../enums/ProjectTypes";
import filterOptions from "../../../utils/filterOptions";
import { IUser } from "../../../pages/User";
import { IDetailedProject, IRequirement } from "../../../pages/Project";
import { ICustomersHoursFactory } from "../../../pages/Dashboards/CustomersHoursFactory";
import { ICustomer } from "../../../pages/Customers";
import ITypePricingProject from "../../../interfaces/ITypePricingProject";
import Table from "../../Table";
import ProgressBar from "../../ProgressBar";
import projects from "../../../pages/Projects";

const { Text } = Typography;

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 10 },
};

type IInstallments =
  | "proposal_approval"
  | "delivery_homologation"
  | "implantation";

type IGenerateInstallments = {
  value: string;
  type: "perc" | "value";
  total_value_hour: number;
};

interface IProposal {
  deadline_payment: string;
  number_installments: string;
  percent?: string;
  project_id: string;
  status?: string;
  implantation?: number;
  proposal_approval?: number;
  delivery_homologation?: number;
  value_hours: number;
  value_installments: number;
  type: string;
  value_project: number;
  number_proposal: string;
  activityToHoursAllocation?: {
    actvity_id: string;
    configsHour: { id: string };
  }[];
  activity: IActivity;
}
interface IProposalData {
  deadline_payment: string;
  number_installments: string;
  percent?: string;
  project_id: string;
  status?: string;
  implantation?: number | string;
  proposal_approval?: number | string;
  delivery_homologation?: number | string;
  value_hours: number | string;
  value_installments: number | string;
  type: string;
  number_proposal: string;
}

interface IOption {
  label: string;
  value: string;
}

export interface IConfigHours {
  id: string;
  value_hours: number;
  description: string;
  status: boolean;
  tag: string | null;
}
export interface IActivity {
  name: string;
  hours: number;
  config_hour_allocation_id?: string;
  configsActivity: { config_hour_allocation_id: string };
  estimator_id: string;
  estimator: IUser;
  id: string;
  project_id: string;
  description: string;
  identifier: string;
  project: IDetailedProject;
}
const ProposalModal: React.FC<IActionModalProps> = ({
  project,
  proposal,
  handleUpdateProject,
}) => {
  const { t } = useTranslation(["generate"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [idGeneratePDF, setIdGeneratePDF] = useState("");
  const [loadingActivity, setLoadingActivity] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [activity, setActivity] = useState<IActivity | undefined>();
  const [updateProposal, setUpdateProposal] = useState<number>(0);
  const [newProposal, setNewProposal] = useState<IProposal>();
  const [optionHours, setOptionsHours] = useState<IOption[]>();
  const [hoursFactory, setHoursFactory] = useState<
    ICustomersHoursFactory | undefined
  >(undefined);
  const [sentProposal, setSentProposal] = useState(false);
  const [proposalApproval, setProposalApproval] = useState({
    percentual: "",
    value: "",
  });
  const [deliveryHomologation, setDeliveryHomologation] = useState({
    percentual: "",
    value: "",
  });
  const [implantation, setImplantation] = useState({
    percentual: "",
    value: "",
  });
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<undefined | number>(undefined);

  const [form] = Form.useForm();

  const { addToast } = useToast();

  useEffect(() => {
    if (project.customer?.id) {
      api
        .get<ICustomer>(`/customers/${project.customer?.id}`)
        .then(response => {
          setHoursFactory(response?.data?.hoursFactory);
        });
    }
  }, [project.customer?.id]);

  const isProjectPricingSeniority = useMemo(() => {
    return project?.pricing === ITypePricingProject.HoursSeniority;
  }, [project]);

  const handleClose = useCallback(() => {
    setVisible(false);

    const proposalProject = ({
      ...project,
      proposal_number_ethic:
        idGeneratePDF || project.proposal_number_ethic || "",
    } as unknown) as IDetailedProject;
    handleUpdateProject(proposalProject);
  }, [idGeneratePDF]);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 402:
          description = "Valores da Parcelas não corresponde a 100%";
          break;
        case 404:
          description = "O projeto não foi encontrado";
          break;
        case 422:
          description = "Número de parcela e parcelas não corresponde";
          break;
        case 408:
          description = "Já existe uma proposta para este projeto";
          break;
        case 409:
          description = "Já existe uma proposta com este número";
          break;
        default:
          description = "Um erro occoreu ao estimar o projeto, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao criar a proposta",
        description,
      });
    },
    [],
  );

  const handleSendRequest = async (data: Partial<IProposal>): Promise<void> => {
    setLoading(true);
    try {
      const payload = {
        ...removeUndefinedKeys({
          ...data,
          proposal_approval: +proposalApproval.percentual || undefined,
          delivery_homologation: +deliveryHomologation.percentual || undefined,
          implantation: +implantation.percentual || undefined,
          project_id: project.id,
        }),
      };
      if (!proposal?.project_id && !idGeneratePDF) {
        await api.post<IProposal>("/proposal/generate", payload);
        setIdGeneratePDF(data?.number_proposal || "");
      } else {
        await api.patch<IProposal>(`/proposal/generate/${project.id}`, {
          ...payload,
          project_id: undefined,
        });
        setIdGeneratePDF(data?.number_proposal || "");
        /* setUpdateProposal(resp.data); */
      }
      setSentProposal(true);
      setVisible(true);

      /* handleClose(); */
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const serverError = err as AxiosError;
        generateErrorToast(serverError.response);
      } else {
        console.log(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: IProposalData) => {
    const newData = {
      ...data,
      proposal_approval: +proposalApproval.percentual,
      delivery_homologation: +deliveryHomologation.percentual,
      implantation: +implantation.percentual,
    };
    try {
      let schema: Yup.ObjectSchema<Yup.Shape<object, IProposalData>>;
      if (data.type === "FACTORY") {
        schema = (Yup.object().shape({
          number_proposal: Yup.string().required(
            t("generate.validation.proposal.required"),
          ),
          number_installments: Yup.number()
            .integer(t("generate.validation.installments.integer"))
            .required(t("generate.validation.installments.required"))
            .when("type", {
              is: type => type === "ALLOCATION" || type === "FACTORY",
              then: Yup.number()
                .max(3, t("generate.validation.installments.max"))
                .min(1, t("generate.validation.installments.min")),
            })
            .when(
              ["proposal_approval", "delivery_homologation", "implantation"],
              // eslint-disable-next-line no-shadow
              (
                proposal_approval,
                delivery_homologation,
                // eslint-disable-next-line no-shadow
                implantation,
                // eslint-disable-next-line no-shadow
                schema,
              ) => {
                const totalInstallments = [
                  proposal_approval || undefined,
                  delivery_homologation || undefined,
                  implantation || undefined,
                ].filter(par => !!par).length;
                return schema.test({
                  name: "installments",
                  message: t("generate.validation.installments.invalid"),
                  test: (value: any) => {
                    return value === totalInstallments;
                  },
                });
              },
            ),
          deadline_payment: Yup.string().required(
            t("generate.validation.deadline_payment.required"),
          ),
          proposal_approval: Yup.number().nullable(),
          delivery_homologation: Yup.number().nullable(),
          implantation: Yup.number().nullable(),
        }) as unknown) as Yup.ObjectSchema<Yup.Shape<object, IProposalData>>;
      } else {
        schema = (Yup.object().shape({
          number_proposal: Yup.string().required(
            t("generate.validation.proposal.required"),
          ),
          number_installments: Yup.number()
            .integer(t("generate.validation.installments.integer"))
            .required(t("generate.validation.installments.required"))
            .when("type", {
              is: type => type === "ALLOCATION" || type === "FACTORY",
              then: Yup.number().min(
                1,
                t("generate.validation.installments.min"),
              ),
            }),
          deadline_payment: Yup.string().required(
            t("generate.validation.deadline_payment.required"),
          ),
          proposal_approval: Yup.number().nullable(),
          delivery_homologation: Yup.number().nullable(),
          implantation: Yup.number().nullable(),
        }) as unknown) as Yup.ObjectSchema<Yup.Shape<object, IProposalData>>;
      }

      await schema.validate(newData, {
        abortEarly: false,
      });
      await handleSendRequest((data as unknown) as Partial<IProposal>);
    } catch (err) {
      setLoading(false);

      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        setValidationErrors(errors);
      }
    }
  };

  async function blobToUint8Array(blob: Blob): Promise<Uint8Array> {
    const arrayBuffer = await blob.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  async function mergePDFs(blob1: Blob, blob2: Blob): Promise<Blob> {
    try {
      const pdf1 = await PDFDocument.load(await blobToUint8Array(blob1));
      const pdf2 = await PDFDocument.load(await blobToUint8Array(blob2));

      const mergedPdf = await PDFDocument.create();

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < pdf1.getPageCount(); i++) {
        // eslint-disable-next-line no-await-in-loop
        const [pdf1Page] = await mergedPdf.copyPages(pdf1, [i]);
        mergedPdf.addPage(pdf1Page);
      }

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < pdf2.getPageCount(); i++) {
        // eslint-disable-next-line no-await-in-loop
        const [pdf2Page] = await mergedPdf.copyPages(pdf2, [i]);
        mergedPdf.addPage(pdf2Page);
      }

      // Salvar o PDF combinado como Blob
      const mergedPdfBytes = await mergedPdf.save();
      const mergedBlob = new Blob([mergedPdfBytes], {
        type: "application/pdf",
      });

      return mergedBlob;
    } catch (error) {
      console.error("Erro ao combinar PDFs:", error);
      throw error;
    }
  }

  const startProgress = () => {
    setProgress(0);
    intervalRef.current = setInterval(() => {
      setProgress(prevProgress => {
        if (prevProgress >= 99) {
          clearInterval(intervalRef.current);
          return 99;
        }
        return prevProgress + 1;
      });
    }, 100);
  };

  const downloadPDF = async (): Promise<void> => {
    setLoadingDownload(old => !old);
    startProgress();
    try {
      const cover = await api.get(
        `/proposal/generate_pdf/${project.id}/cover`,
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/pdf",
          },
        },
      );
      const pages = await api.get(
        `/proposal/generate_pdf/${project.id}/pages`,
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/pdf",
          },
        },
      );

      const combinedBlob = await mergePDFs(cover.data, pages.data);

      const url = URL.createObjectURL(combinedBlob);

      const anchor = document.createElement("a");
      anchor.style.display = "none";
      anchor.href = url;
      anchor.download = `PROPOSTA COMERCIAL - ${idGeneratePDF}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingDownload(old => !old);
      setProgress(100);
      clearInterval(intervalRef.current);
    }
  };

  const warning = useCallback(() => {
    Modal.warning({
      title: "Nenhuma atividade",
      content:
        "O projeto tem que ter pelo menos uma atividade para ser estimado.",
    });
  }, []);

  const handleShowModal = useCallback(() => {
    /* if (estimations.length > 0) {
      setVisible(true);
    } else {
      warning();
    } */
    setVisible(true);
  }, [warning]);

  const pricingProject = useMemo(() => {
    return project.requirements.reduce(
      (acc, req) =>
        acc + req.hours * (req.hoursFactory?.configHour.value_hour || 0),
      0,
    );
  }, [project]);

  function generateValueInstallments(
    { value, type, total_value_hour }: IGenerateInstallments,
    update: React.Dispatch<
      React.SetStateAction<{ percentual: string; value: string }>
    >,
  ): void {
    let total = +total_value_hour;
    if (!visible) return;
    if (isProjectPricingSeniority) {
      total = pricingProject;
    }

    if (type === "value") {
      update(oldProposal => {
        const maxValue =
          -(+oldProposal.value || 0) +
          +proposalApproval.value +
          +deliveryHomologation.value +
          +implantation.value +
          +value;
        if (maxValue > (proposal?.value_project || 0)) return oldProposal;
        const newPercentual = !+value
          ? oldProposal.percentual
          : (+value * 100) / total;
        return { percentual: String(newPercentual), value };
      });
    }
    if (type === "perc") {
      update(oldProposal => {
        const maxPercentual =
          -(+oldProposal.percentual || 0) +
          +proposalApproval.percentual +
          +deliveryHomologation.percentual +
          +implantation.percentual +
          +value;
        if (maxPercentual > 100) return oldProposal;
        const newValue = !+value ? oldProposal.value : (+value / 100) * total;
        return { value: String(newValue), percentual: value };
      });
    }
  }

  function createInstallments(
    inputValue: string,
    typeInput: "perc" | "value",
    nameInstallments: IInstallments,
  ): void {
    const proposalValue = updateProposal || 0;
    const totalProject = proposalValue || proposal?.value_project || 0;
    const TypeInstallments = {
      proposal_approval: (info: IGenerateInstallments) =>
        generateValueInstallments(info, setProposalApproval),
      delivery_homologation: (info: IGenerateInstallments) =>
        generateValueInstallments(info, setDeliveryHomologation),
      implantation: (info: IGenerateInstallments) =>
        generateValueInstallments(info, setImplantation),
    };

    TypeInstallments[nameInstallments]({
      value: inputValue,
      type: typeInput,
      total_value_hour: totalProject,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getProposal = async () => {
    setLoadingActivity(old => !old);
    try {
      setIdGeneratePDF(proposal?.number_proposal || "");
      setActivity(newProposal?.activity);
      if (newProposal?.activity) {
        const config = await api.get(`/config_hours_allocation`);
        setOptionsHours(
          config?.data
            ?.filter((usr: IConfigHours) => usr?.status)
            ?.map((usr: IConfigHours) => ({
              label: `${usr?.tag ? `${usr?.tag} - ` : ""}${usr?.description}`,
              value: usr?.id,
            })),
        );
      }

      const installments: { value: number; key: IInstallments }[] = [
        { value: proposal?.implantation || 0, key: "implantation" },
        {
          value: proposal?.proposal_approval || 0,
          key: "proposal_approval",
        },
        {
          value: proposal?.delivery_homologation || 0,
          key: "delivery_homologation",
        },
      ];
      setUpdateProposal(newProposal?.value_project || 0);
      installments.map(installment => {
        return createInstallments(
          `${installment?.value || ""}`,
          "perc",
          installment.key,
        );
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const serverError = err as AxiosError;
        generateErrorToast(serverError.response);
      } else {
        console.log(err);
      }
    }
    setLoadingActivity(old => !old);
  };

  const existProposal = useCallback(async () => {
    try {
      const response = await api.get(`/proposal/generate/${project?.id}`);
      setNewProposal(oldProposal => {
        return { ...oldProposal, ...response.data };
      });
    } catch (e) {
      console.log(e);
    }
  }, [proposal, project, idGeneratePDF]);

  useEffect(() => {
    if (visible) getProposal().then();
  }, [visible, newProposal, project]);

  useEffect(() => {
    if (visible) existProposal().then();
  }, [visible, proposal]);

  const validet = useMemo(() => {
    return (
      project.projectType.acronym !== ProjectTypes.Factory &&
      project.projectType.acronym !== ProjectTypes.Allocation
    );
  }, [project]);

  const requirementsColumns: ColumnsType<IRequirement> = [
    {
      dataIndex: "description",
      title: "Descrição",
    },
    {
      dataIndex: "hours",
      title: "Horas Atividade",
    },
    {
      dataIndex: "hoursFactory",
      title: "Hora Fábrica",
      render: (_: string, record: IRequirement) => {
        const hours = new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(record?.hoursFactory?.configHour.value_hour || 0);

        return hours || "";
      },
    },
    {
      dataIndex: "hoursFactoryDescription",
      title: "Descrição Hora Fábrica",
      width: "200px",
      render: (_: string, record: IRequirement) => {
        return record?.hoursFactory?.configHour?.description || "";
      },
    },
  ];

  const ProjectHourSeniority = useMemo(() => {
    return project?.pricing === ITypePricingProject.HoursSeniority;
  }, [project]);

  return validet ? (
    <></>
  ) : (
    <>
      <Modal
        title={t("generate.title")}
        visible={visible}
        width={ProjectHourSeniority ? 800 : 600}
        onOk={() => form.submit()}
        onCancel={() => handleClose()}
        okButtonProps={{
          htmlType: "submit",
          loading,
        }}
        getContainer={false}
        forceRender
        okText="salvar"
        cancelText="fechar"
      >
        <Form form={form} onFinish={handleSubmit}>
          <Flex>
            <FormItem
              name="number_installments"
              required
              label={t("generate.number_installments")}
              errors={validationErrors}
              initialValue={proposal?.number_installments || 0}
            >
              <Input type="number" />
            </FormItem>
          </Flex>
          <Flex>
            <FormItem
              name="number_proposal"
              required
              label={t("generate.number_proposal")}
              errors={validationErrors}
              initialValue={proposal?.number_proposal || ""}
            >
              <Input placeholder="Número da Proposta" />
            </FormItem>
          </Flex>
          <Flex>
            <FormItem
              name="deadline_payment"
              required
              label={t("generate.deadline_payment")}
              errors={validationErrors}
              initialValue={proposal?.deadline_payment || 60}
            >
              <Input type="number" />
            </FormItem>
          </Flex>

          <FormItem
            name="type"
            hidden
            initialValue={project.projectType.acronym}
          >
            <Input />
          </FormItem>
          {project?.projectType?.acronym === "ALLOCATION" && (
            <>
              {activity && (
                <Flex style={{ marginBottom: "24px" }}>
                  <TotalHours>
                    {activity.name} - {activity.hours}h -
                  </TotalHours>
                  <FormItem
                    errors={validationErrors}
                    name="config_hour_allocation_id"
                    style={{
                      marginBottom: 0,
                      marginLeft: "5px",
                      maxWidth: "380px",
                      width: "100%",
                    }}
                  >
                    <Select
                      allowClear
                      showSearch
                      options={optionHours}
                      filterOption={filterOptions}
                      loading={loadingActivity}
                      defaultValue={
                        proposal?.activity?.config_hour_allocation_id || ""
                      }
                      style={{ width: "340px" }}
                    />
                  </FormItem>
                </Flex>
              )}
            </>
          )}
          {project?.projectType?.acronym === "FACTORY" && (
            <>
              <InputContent rm_margin={!!+proposalApproval?.percentual}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <Label required htmlFor="approval_percentual">
                  {t("generate.percentage.proposal_approval")}(%):
                </Label>
                <InputDefault
                  id="approval_percentual"
                  name="approval_percentual"
                  placeholder="0"
                  onChange={
                    e =>
                      createInstallments(
                        e.target.value,
                        "perc",
                        "proposal_approval",
                      )
                    // eslint-disable-next-line react/jsx-curly-newline
                  }
                  value={proposalApproval.percentual}
                />
              </InputContent>
              <InputContent
                hidden={
                  proposalApproval.percentual === "" ||
                  proposalApproval.percentual === "0"
                }
              >
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <Label htmlFor="approval_value">
                  {t("generate.value.proposal_approval")}:
                </Label>
                <InputDefault
                  id="approval_value"
                  name="approval_value"
                  onChange={
                    e =>
                      createInstallments(
                        e.target.value,
                        "value",
                        "proposal_approval",
                      )
                    // eslint-disable-next-line react/jsx-curly-newline
                  }
                  value={proposalApproval.value}
                />
              </InputContent>
              <InputContent rm_margin={!!+deliveryHomologation?.percentual}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <Label required htmlFor="homologation_percentual">
                  {t("generate.percentage.delivery_homologation")}(%):
                </Label>
                <InputDefault
                  id="homologation_percentual"
                  name="homologation_percentual"
                  placeholder="0"
                  onChange={
                    e =>
                      createInstallments(
                        e.target.value,
                        "perc",
                        "delivery_homologation",
                      )
                    // eslint-disable-next-line react/jsx-curly-newline
                  }
                  value={deliveryHomologation.percentual}
                />
              </InputContent>
              <InputContent
                hidden={
                  deliveryHomologation.percentual === "" ||
                  deliveryHomologation.percentual === "0"
                }
              >
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <Label htmlFor="homologation_value">
                  {t("generate.value.delivery_homologation")}:
                </Label>
                <InputDefault
                  id="homologation_value"
                  name="homologation_value"
                  onChange={
                    e =>
                      createInstallments(
                        e.target.value,
                        "value",
                        "delivery_homologation",
                      )
                    // eslint-disable-next-line react/jsx-curly-newline
                  }
                  value={deliveryHomologation.value}
                />
              </InputContent>
              <InputContent rm_margin={!!+implantation?.percentual}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <Label required htmlFor="implantation_percentual">
                  {t("generate.percentage.implantation")}(%):
                </Label>
                <InputDefault
                  id="implantation_percentual"
                  name="implantation_percentual"
                  placeholder="0"
                  onChange={
                    e =>
                      createInstallments(e.target.value, "perc", "implantation")
                    // eslint-disable-next-line react/jsx-curly-newline
                  }
                  value={implantation.percentual}
                />
              </InputContent>
              <InputContent
                hidden={
                  implantation.percentual === "" ||
                  implantation.percentual === "0"
                }
              >
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <Label htmlFor="implantation_value">
                  {t("generate.value.implantation")}:
                </Label>
                <InputDefault
                  id="implantation_value"
                  name="implantation_value"
                  onChange={
                    e =>
                      createInstallments(
                        e.target.value,
                        "value",
                        "implantation",
                      )
                    // eslint-disable-next-line react/jsx-curly-newline
                  }
                  value={implantation.value}
                />
              </InputContent>
            </>
          )}
          {project.projectType.acronym === "FACTORY" && !hoursFactory?.value && (
            <Label style={{ color: "red" }} required>
              hora fábrica não associada ao cliente
            </Label>
          )}
        </Form>

        {isProjectPricingSeniority && (
          <Table
            columns={requirementsColumns}
            data={project?.requirements}
            footer={() => (
              <>
                <Flex style={{ justifyContent: "space-between" }}>
                  <div>Total</div>
                  <div>
                    {new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(pricingProject)}
                  </div>
                </Flex>
              </>
            )}
            title="Detalhamento"
            pagination={false}
          />
        )}

        {proposal?.project_id || sentProposal ? (
          <ButtonDownload
            type="button"
            loading={loadingDownload}
            style={{
              position: "relative",
              pointerEvents: loadingDownload ? "none" : "auto",
            }}
            onClick={() => downloadPDF()}
            fallback={<ProgressBar progress={progress} />}
          >
            <p style={{ color: "#fff", margin: "auto" }}>
              {t("generate.button.download_pdf")}
            </p>
          </ButtonDownload>
        ) : (
          <></>
        )}
      </Modal>
      <Button type="primary" onClick={handleShowModal}>
        {t("Geração de Proposta")}
      </Button>
    </>
  );
};

export default ProposalModal;
